<template>
  <div :class="$style.details">
    <div>
      <h5 :class="$style.detailsHeading">
        {{ summary.total }}
      </h5>
      <p :class="$style.caption">{{ $t('overview.header.properties') }}</p>
    </div>
    <div>
      <h5 :class="$style.detailsHeading">{{ summary.estimatedPortfolioValue ?? '-' }} CHF</h5>
      <p :class="$style.caption">{{ $t('overview.header.estimation') }}</p>
    </div>
    <div>
      <h5 :class="$style.detailsHeading">
        <HgIcon
          v-if="summary.trends"
          :data-testid="`trends-icon-${summary.trends}`"
          :icon="summary.trends === 'up' ? 'ArrowUp' : 'ArrowDown'"
          :class="$style[summary.trends]"
        />{{ summary.growthRate ?? '-' }} %
      </h5>
      <p :class="$style.caption">
        {{ $t('overview.header.appreciation') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatCompactNumber } from '@/helpers/Number';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { OfferType } from '@/models/valuationFunnel';
import { TEXT_PLACEHOLDER } from '@/types';
import { HgIcon, HgSupportedLanguage, hgNumber } from '@smg-real-estate/domus';
import isNil from 'lodash.isnil';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();

export interface Props {
  propertiesData: PropertyValuationResult[];
}

const props = defineProps<Props>();

const calculatePriceGrowth = (property: PropertyValuationResult): number => {
  if (!property?.priceEvolution?.data || property.priceEvolution.data.length < 2) {
    return 0;
  }

  const data = property.priceEvolution.data;
  const firstValue = data[0].indexValue;
  const lastValue = data[data.length - 1].indexValue;

  if (firstValue === 0) {
    return 0;
  }

  return ((lastValue - firstValue) / firstValue) * 100;
};

const summary = computed<{
  total: string;
  estimatedPortfolioValue: string;
  growthRate: string;
  trends: '' | 'up' | 'down';
}>(() => {
  const propertiesData = props.propertiesData ?? [];
  const owningProperties = propertiesData.filter(
    (property) => property.offerType === OfferType.Own || property.offerType === OfferType.Selling,
  );

  const estimatedPortfolioValueCalc =
    owningProperties.length > 0
      ? owningProperties.reduce(
          (acc: number, curr: PropertyValuationResult) => acc + (Number(curr.appraisalPrice) || 0),
          0,
        )
      : undefined;

  const growthRateCalc =
    owningProperties.length > 0
      ? owningProperties
          .map((property) => calculatePriceGrowth(property))
          .reduce((acc: number, growth: number) => acc + growth, 0)
      : undefined;

  const isEmpty = isNil(estimatedPortfolioValueCalc);
  return {
    total: isEmpty
      ? TEXT_PLACEHOLDER
      : (propertiesData ?? [])
          .filter(
            (item) => item.offerType === OfferType.Own || item.offerType === OfferType.Selling,
          )
          .length.toString(),
    estimatedPortfolioValue: isEmpty
      ? TEXT_PLACEHOLDER
      : '~ ' +
        formatCompactNumber(estimatedPortfolioValueCalc, locale.value as HgSupportedLanguage),
    growthRate: isEmpty
      ? TEXT_PLACEHOLDER
      : hgNumber(Math.round(growthRateCalc! * 100) / 100, locale.value as HgSupportedLanguage),
    trends: isEmpty || growthRateCalc === 0 ? '' : growthRateCalc! > 0 ? 'up' : 'down',
  };
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.details {
  display: flex;
  flex-direction: row;
  gap: $space-100;
  margin-top: $space-75;
}

.detailsHeading {
  display: flex;
  align-items: center;
  @include heading-5;
  @include vertical-margin;

  .up {
    @include color-icon($color-teal-100);
  }

  .down {
    @include color-icon($color-orange-90);
  }
}

.caption {
  @include caption;
  @include vertical-margin;
}
</style>
